import { useCallback } from 'react';
import { CommonActions } from '@react-navigation/native';
import { getBankConnections, getSyncingStatus, listProviderAccounts, resetLinkingConnection, setConnectionsStatus, setLinkingConnection, } from 'actions';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { LIST_PROVIDER_ACCOUNTS_SUCCESS } from 'actions/types';
import { ConnectionUpdateFlow, ConnectionUpdateStep, } from 'utils/types/navigation';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import { selectConnections } from 'reducers/selectors';
import useAppRating from 'hooks/useAppRating';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import { isUnlocked } from 'utils';
import { selectUserTier } from 'features/premium/selectors/tier';
import { benefits } from 'features/premium/benefits';
import { selectIsDefaultSpace } from 'features/spaces/reducers/selectors';
import { getMySpaces } from 'features/spaces/actions';
import { isIOS } from '../../../constants';
import { logBankAdded } from '../analytics/logBankAdded';
export const useLinkedConnection = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const refreshAccounts = useRefreshAccounts();
    const rateApp = useAppRating();
    const navigateToSuccess = useCallback((bankInfo) => {
        const isInEmmaSetupFlow = navigation
            .getState()
            .routes.some((route) => route.name === 'SetUpEmmaSteps' ||
            route.name === 'ContinueEmmaSetUp');
        setTimeout(() => dispatch(resetLinkingConnection()), 300);
        dispatch(setConnectionsStatus(true));
        dispatch(getSyncingStatus());
        dispatch(getMySpaces());
        navigation.navigate('Success', {
            subTitle: `You’ve successfully connected your ${bankInfo?.name || 'bank'}! You can manage your connections anytime in settings`,
            onContinue: () => {
                navigation.goBack();
                return true;
            },
            ...(!isInEmmaSetupFlow
                ? {
                    secondaryButtonText: 'Manage bank connections',
                    onSecondaryButtonPress: () => {
                        navigation.goBack();
                        navigation.navigate('Logins', undefined, { pop: true });
                        rateApp();
                        return true;
                    },
                }
                : {}),
            buttonText: 'Done',
        });
    }, [rateApp]);
    const hideModal = useCallback(() => {
        setTimeout(() => LoadingAppViewModal.hide(), 
        // Waiting for navigation animation
        isIOS ? 300 : 100);
    }, []);
    const handleNext = useCallback(async (bankConnection) => {
        const res = await dispatch(listProviderAccounts(bankConnection));
        if (res?.type === LIST_PROVIDER_ACCOUNTS_SUCCESS) {
            // Imported accounts change the balance history graphs
            refreshAccounts(res.payload.accounts.map((account) => account.type));
        }
        const state = store.getState();
        const { importedAccounts } = state.linkBank;
        if (res?.type !== LIST_PROVIDER_ACCOUNTS_SUCCESS ||
            !importedAccounts?.new.length) {
            navigateToSuccess(bankConnection.bankInfo);
        }
        else {
            const steps = [];
            steps.push(ConnectionUpdateStep.REVIEW);
            const addedBusinessAccount = importedAccounts.new.some((account) => account.isBusiness);
            if (addedBusinessAccount &&
                !isUnlocked(selectUserTier(state), benefits.business.minTier)) {
                steps.push(ConnectionUpdateStep.BUSINESS);
            }
            if (!selectIsDefaultSpace(state)) {
                steps.push(ConnectionUpdateStep.SPACE);
            }
            navigation.navigate('ConnectionUpdate', {
                flow: ConnectionUpdateFlow.LINK,
                steps,
                stepIndex: 0,
            });
        }
        hideModal();
    }, [hideModal, navigateToSuccess, refreshAccounts]);
    const handleLinkedConnection = useCallback((linkingBankId) => {
        const state = store.getState();
        const { banks, linkingConnection } = state.linkBank;
        const bankInfo = linkingConnection?.bankInfo ||
            banks.find((bank) => bank.id === linkingBankId);
        const connectionsIds = new Set(selectConnections(state).map(({ id }) => id));
        LoadingAppViewModal.show({ title: 'Importing your accounts' });
        if (!linkingConnection) {
            // This is a new connection, not logged before
            logBankAdded(bankInfo);
        }
        navigation.dispatch((state) => {
            const routes = state.routes.filter((route) => route.name !== 'ConnectionUpdate' &&
                route.name !== 'Consent' &&
                route.name !== 'ChooseBank');
            if (state.type !== 'stack' || routes.length === state.routes.length) {
                return CommonActions.reset(state);
            }
            return CommonActions.reset({
                ...state,
                routes,
                index: routes.length - 1,
            });
        });
        if (linkingConnection) {
            handleNext(linkingConnection);
            return;
        }
        const MAX_POLLING_TIME = 10000;
        const timestamp = new Date().getTime();
        const pollConnections = async () => {
            await dispatch(getBankConnections());
            const conns = selectConnections(store.getState());
            if (conns.length > connectionsIds.size) {
                const newConnections = conns.filter(({ id }) => !connectionsIds.has(id));
                if (newConnections.length === 1 && newConnections[0]) {
                    const conn = { ...newConnections[0], accounts: [] };
                    dispatch(setLinkingConnection(conn));
                    handleNext(conn);
                    return;
                }
                navigateToSuccess(bankInfo);
                hideModal();
                return;
            }
            if (new Date().getTime() - MAX_POLLING_TIME > timestamp) {
                navigateToSuccess(bankInfo);
                hideModal();
                return;
            }
            setTimeout(pollConnections, 1000);
        };
        pollConnections();
    }, [handleNext, hideModal]);
    return { handleLinkedConnection, navigateToSuccess };
};
export default useLinkedConnection;
